<template>
  <div
    ref="frame"
    data-slot="lazy-image"
    :class="[ 'relative flex overflow-hidden bg-muted select-none', (isLoading || isError) && 'ring-1 ring-border ring-inset' ]"
  >
    <template v-if="isVisible">
      <img
        v-if="!isError"
        ref="img"
        class="size-full object-cover"
        :src="src"
        :crossorigin="replaceColor ? 'anonymous' : undefined"
        alt=""
        @load="onLoad"
        @error="onError"
      />

      <canvas v-if="replaceColor" ref="canvas" class="hidden" />

      <loader-circle-icon v-if="isLoading" class="absolute top-1/2 left-1/2 size-4 -translate-1/2 animate-spin text-muted-foreground" />
      <image-off-icon v-else-if="isError" class="absolute top-1/2 left-1/2 size-6 -translate-1/2 text-muted-foreground/60" />
    </template>
  </div>
</template>

<script>
import { ImageOff, LoaderCircle } from '@lucide/vue';

const TOLERANCE = 170;

// replace-color recolors the 8ad blue in an image to the fork's primary color.
const SOURCE_COLOR = '#2400ff';
const FORK_PRIMARY_COLORS = {
  turisztikai: '#617087',
  shoprenter: '#85b218',
};

/** Image loaded once it scrolls into view. Size and corner radius come from the caller. */
export default {
  name: 'LazyImage',
  components: {
    ImageOffIcon: ImageOff,
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    src: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    replaceColor: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data: () => ({
    observer: null,
    isVisible: false,
    isLoading: true,
    isError: false,
  }),
  computed: {
    sourceColor() {
      return hexToRgb(SOURCE_COLOR);
    },
    targetColor() {
      return hexToRgb(FORK_PRIMARY_COLORS[process.env.VUE_APP_FORK] || SOURCE_COLOR);
    },
  },
  watch: {
    src: {
      immediate: true,
      handler(val) {
        this.isLoading = !!val;
        this.isError = !val;
      },
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(entry => {
      if (entry[0].isIntersecting) {
        this.isVisible = true;
        this.observer.disconnect();
      }
    });

    this.observer.observe(this.$refs.frame);
  },
  beforeUnmount() {
    this.observer.disconnect();
  },
  methods: {
    onLoad() {
      if (this.replaceColor) {
        this.applyColorReplacement();
      }
      else {
        this.isLoading = false;
      }
    },
    onError() {
      this.isError = true;
      this.isLoading = false;
    },
    applyColorReplacement() {
      const img = this.$refs.img;
      const canvas = this.$refs.canvas;

      if (!img || !canvas) {
        this.isLoading = false;
        return;
      }

      const ctx = canvas.getContext('2d');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      let imageData;
      try {
        imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      }
      catch (e) {
        // eslint-disable-next-line no-console
        console.warn('LazyImage: színcsere sikertelen (CORS?):', e);
        this.isLoading = false;
        return;
      }

      const data = imageData.data;
      const { r: sr, g: sg, b: sb } = this.sourceColor;
      const { r: tr, g: tg, b: tb } = this.targetColor;

      for (let i = 0; i < data.length; i += 4) {
        const dist = colorDistance(data[i], data[i + 1], data[i + 2], sr, sg, sb);

        if (dist >= TOLERANCE) {
          continue;
        }

        const blend = smoothstep(dist / TOLERANCE);

        data[i]     = Math.min(255, Math.max(0, Math.round(data[i]     + (1 - blend) * (tr - sr))));
        data[i + 1] = Math.min(255, Math.max(0, Math.round(data[i + 1] + (1 - blend) * (tg - sg))));
        data[i + 2] = Math.min(255, Math.max(0, Math.round(data[i + 2] + (1 - blend) * (tb - sb))));
      }

      ctx.putImageData(imageData, 0, 0);
      img.src = canvas.toDataURL('image/png');
      this.isLoading = false;
    },
  },
};

function smoothstep(x) {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
}

function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}
</script>
